import { c as _c } from "react/compiler-runtime";
import { alpha, Box } from "@mui-wrapper";
import { HEX_ALPHA } from "@flat-ui";
import { useMemo } from "react";

/** Diameter (px) of the solid core dot rendered at the marker location. */
const PULSE_CORE_SIZE_PX = 8;

/** Maximum radius (px) the pulsing ring expands to before fading out. */
const PULSE_MAX_SPREAD_PX = 12;

/** Duration of a single pulse cycle. */
const PULSE_DURATION = "1.6s";
/**
 * A CSS pulse ring drawn over the map at a highlighted marker's projected screen
 * position. A soft ring expands and fades out from the marker, giving a "radar
 * ping" cue when a project is hovered from the list.
 *
 * The motion lives entirely in CSS (GPU-accelerated, no WebGL cost) while the
 * marker itself stays a static recolored graphic on the map, so the ring works
 * identically in 2D and 3D and never fights the marker's rendering.
 */
export function MarkerPulseRing(t0) {
  const $ = _c(20);
  const {
    x,
    y,
    color
  } = t0;
  let t1;
  if ($[0] !== color) {
    t1 = alpha(color, HEX_ALPHA.alphaA6);
    $[0] = color;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const pulseColorStart = t1;
  let t2;
  if ($[2] !== color) {
    t2 = alpha(color, 0);
    $[2] = color;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const pulseColorEnd = t2;
  let t3;
  if ($[4] !== x || $[5] !== y) {
    t3 = {
      position: "absolute",
      left: x,
      top: y,
      transform: "translate(-50%, -50%)",
      pointerEvents: "none",
      zIndex: 1
    };
    $[4] = x;
    $[5] = y;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = `0 0 0 0px ${pulseColorStart}`;
  let t5;
  if ($[7] !== t4) {
    t5 = {
      boxShadow: t4
    };
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = `0 0 0 ${PULSE_MAX_SPREAD_PX}px ${pulseColorEnd}`;
  let t7;
  if ($[9] !== t6) {
    t7 = {
      boxShadow: t6
    };
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5 || $[12] !== t7) {
    t8 = {
      "0%": t5,
      "100%": t7
    };
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== color || $[15] !== t8) {
    t9 = <Box sx={{
      width: `${PULSE_CORE_SIZE_PX}px`,
      height: `${PULSE_CORE_SIZE_PX}px`,
      borderRadius: "50%",
      backgroundColor: color,
      animation: `marker-pulse-animation ${PULSE_DURATION} ease-out infinite`,
      "@keyframes marker-pulse-animation": t8
    }} />;
    $[14] = color;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t3 || $[18] !== t9) {
    t10 = <Box sx={t3}>{t9}</Box>;
    $[17] = t3;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}